// TODO: entire file is duplicated

import { Box, Breadcrumbs, Grid, Link, Typography } from "@mui/material";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Code } from "~/components/code";
import { Home as HomeIcon } from "@mui/icons-material";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { Markdown } from "~/components/markdown";
import { Flex } from "~/components/Flex";
import { TabsVariant, VariantIcon } from "~/components/ui-library";
import { PolicyIdToMrn, PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { Space } from "~/lib/types";
import { useGetPolicyQuery, useLoadMqueryQuery } from "~/operations";
import { setDocumentTitle } from "~/lib/commonUtils";
import { SectionHeading } from "~/components/DetailsPage/components";
import { CheckRemediation } from "~/pages/space/security/components/Check/CheckRemediation/CheckRemediation";

const getQueryMrn = (policyId: string, queryId: string) => {
  let mrn = "//policy.api.mondoo.app";
  if (policyId.includes("/")) {
    const namespace = policyId.split("/")[0];
    mrn = `${mrn}/spaces/${namespace}`;
  }
  return `${mrn}/queries/${queryId}`;
};

export type PoliciesAddQueryPageProps = {
  space: Space;
};

export function PoliciesAddQueryPage({ space }: PoliciesAddQueryPageProps) {
  const { policyId = "", queryId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const policyMrn = PolicyIdToMrn(policyId);
  const queryMrn = getQueryMrn(policyId, queryId);
  const selectedVariantId = searchParams.get("variantQuery");

  const { data, loading, error } = useGetPolicyQuery({
    variables: { input: { mrn: policyMrn, spaceMrn: space.mrn } },
    skip: !policyId,
  });

  const {
    data: mqueryData,
    loading: mqueryLoading,
    error: mqueryError,
  } = useLoadMqueryQuery({
    variables: { input: { mrn: queryMrn } },
    skip: !policyId || !queryId,
  });

  const isLoading = mqueryLoading || loading;

  const loadingFailed = !policyId || error || !data?.policy || mqueryError;
  const policy = data?.policy;
  const mquery = mqueryData?.mquery;

  const variants = [
    ...(
      mquery?.variants?.map((variant) => ({
        label: variant?.title || "",
        value: variant?.id || "",
        icon: <VariantIcon type={variant?.icon || ""} />,
      })) || []
    ).sort((a, b) => (a.label > b.label ? 1 : -1)),
  ];

  const selectedVariant =
    variants.find((v) => v.value === selectedVariantId) || variants[0];

  const variantQueries =
    mquery?.variantQueries?.flatMap((vq) => vq ?? []) || [];
  const visibleVariantQueries = variantQueries.flatMap((vq) => {
    if (!selectedVariant) return vq;
    return vq.filterID === selectedVariant.value ? [vq] : [];
  });

  const visibleQueries =
    variantQueries.length > 0 ? visibleVariantQueries : mquery ? [mquery] : [];

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?spaceId=${space.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/findings/policies"
      component={RouterLink}
      to={`/space/findings/policies?spaceId=${space.id}`}
    >
      Policies
    </Link>,
    <Link
      key="/space/findings/policies/add"
      component={RouterLink}
      to={`/space/findings/policies/add?spaceId=${space.id}`}
    >
      Add Policies
    </Link>,
    <Link
      key="/space/findings/policies/add/policyId"
      component={RouterLink}
      to={`/space/findings/policies/add/${PolicyMrnToURIEncodedId(policy?.mrn || "")}?spaceId=${space.id}`}
    >
      {policy?.name}
    </Link>,
    <Typography key="/space/findings/policies/add/policyId/queries/queryId">
      {mquery?.title}
    </Typography>,
  ];

  setDocumentTitle([mquery?.title, policy?.name, "Policies", "Security"]);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {mquery && (
        <>
          <Box
            id="mquery-header"
            mb={3}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              textTransform="uppercase"
              sx={{ mb: 3 }}
            >
              {mquery.title}
            </Typography>
            <Flex id="mquery-actions" alignItems="center" gap={4}></Flex>
          </Box>
          {variants.length > 0 && (
            <TabsVariant
              options={variants}
              selectedTab={selectedVariant.value}
              onTabChange={(variantId) => {
                variantId.length > 0
                  ? searchParams.set("variantQuery", variantId)
                  : searchParams.delete("variantQuery");
                setSearchParams(searchParams);
              }}
              mb={3}
            />
          )}
        </>
      )}

      {isLoading && <LoadingPage what="Query" />}
      {!isLoading && loadingFailed && <LoadingFailedPage what="Query" />}
      {!isLoading && mquery && (
        <Box>
          <Box className="section">
            <Box className="section-content">
              <Markdown source={mquery.docs?.desc || ""} />
              <SectionHeading heading="Queries" />
              {visibleQueries.map((q) => {
                const remediations = q.docs?.remediations;
                const hasRemediations =
                  remediations?.__typename === "Remediations" &&
                  remediations.entries.length > 0;

                return (
                  <Box key={q.mrn}>
                    <Box>
                      <Typography component="h4" fontWeight={700}>
                        MQL
                      </Typography>
                      <Code className="coffeescript">{q.mql}</Code>
                    </Box>
                    {hasRemediations && (
                      <Grid size={12}>
                        <SectionHeading heading="Remediation" />
                        <CheckRemediation entries={remediations.entries} />
                      </Grid>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
